import React from 'react';
import { Box, Typography, IconButton, Chip } from '@mui/material';
import { MoreHoriz as MoreHorizIcon } from '@mui/icons-material';
import { Order } from '../../../services';
import { Table, type TableColumn } from '../../Common';
import './VendorOrdersTable.css';

interface VendorOrdersTableProps {
  orders: Order[];
  loading: boolean;
  onOrderClick?: (order: Order) => void;
}

const VendorOrdersTable: React.FC<VendorOrdersTableProps> = ({
  orders,
  loading,
  onOrderClick,
}) => {

  const getStatusConfig = (status: string) => {
    // Normalize status to lowercase for matching
    const normalizedStatus = status.toLowerCase();
    const configs: Record<string, { label: string; backgroundColor: string; color: string }> = {
      pending: { label: 'Pending', backgroundColor: 'var(--palette-light-background-tertiary-value-hex)', color: 'var(--palette-light-text-foreground-value-hex)' },
      approved: { label: 'Approved', backgroundColor: 'var(--palette-light-background-secondary-value-hex)', color: 'var(--palette-light-text-primary-value-hex)' },
      cancelled: { label: 'Cancelled', backgroundColor: 'var(--palette-light-background-tertiary-value-hex)', color: 'var(--palette-light-text-foreground-value-hex)' },
      declined: { label: 'Declined', backgroundColor: 'var(--palette-light-background-tertiary-value-hex)', color: 'var(--palette-light-text-foreground-value-hex)' },
      refunded: { label: 'Refunded', backgroundColor: 'var(--palette-light-background-tertiary-value-hex)', color: 'var(--palette-light-text-foreground-value-hex)' },
      changing: { label: 'Changing', backgroundColor: 'var(--palette-light-brand-secondary-value-hex)', color: 'var(--palette-light-text-foreground-value-hex)' },
      'in-progress': { label: 'In-progress', backgroundColor: 'var(--palette-light-brand-tertiary-value-hex)', color: 'var(--palette-light-text-foreground-value-hex)' },
      delivery: { label: 'Delivery', backgroundColor: 'var(--palette-light-brand-primary-value-hex)', color: 'var(--palette-light-text-foreground-value-hex)' },
      completed: { label: 'Completed', backgroundColor: 'var(--palette-light-background-tertiary-value-hex)', color: 'var(--palette-light-text-foreground-value-hex)' },
      // Legacy support for "refund" (without "ed")
      refund: { label: 'Refunded', backgroundColor: 'var(--palette-light-background-tertiary-value-hex)', color: 'var(--palette-light-text-foreground-value-hex)' },
    };
    return configs[normalizedStatus] || configs.pending;
  };

  const columns: TableColumn<Order>[] = [
    {
      key: 'id',
      label: 'ID',
      width: 1,
      render: (order: Order) => (
        <Box className="vendor-orders-table-cell-id">
          <Typography className="vendor-orders-table-text-primary" component="div">
            {order.id || order.orderNumber ? `...${(order.id || order.orderNumber).slice(-3).toUpperCase()}` : 'N/A'}
          </Typography>
          <Typography className="vendor-orders-table-text-secondary" component="div">
            {order.orderDate}, {order.orderTime}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'customer',
      label: 'Customer',
      width: 1.5,
      render: (order: Order) => (
        <Box className="vendor-orders-table-cell-customer">
          <Typography className="vendor-orders-table-text-primary" component="div">
            {order.customerName}
          </Typography>
          <Typography className="vendor-orders-table-text-secondary" component="div">
            {order.customerPhone}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'event',
      label: 'Event',
      width: 1.5,
      render: (order: Order) => (
        <Box className="vendor-orders-table-cell-event">
          <Typography className="vendor-orders-table-text-primary" component="div">
            {order.eventDate}, {order.eventTime}
          </Typography>
          <Typography className="vendor-orders-table-text-secondary" component="div">
            {order.eventLocation}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'product',
      label: 'Product',
      width: 2,
      render: (order: Order) => (
        <Box className="vendor-orders-table-cell-product">
          <Typography className="vendor-orders-table-text-primary" component="div">
            {order.productName}
          </Typography>
          <Typography className="vendor-orders-table-text-secondary" component="div">
            {order.productDetails}
          </Typography>
          {order.additionalProducts && (
            <Typography className="vendor-orders-table-text-brand" component="div">
              {order.additionalProducts}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'total',
      label: 'Total',
      width: 1,
      render: (order: Order) => (
        <Box className="vendor-orders-table-cell-total">
          {order.originalTotal ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
              <Typography 
                className="vendor-orders-table-text-primary" 
                component="div"
                sx={{ 
                  textDecoration: 'line-through',
                  color: 'var(--palette-light-text-secondary-value-hex)',
                  fontSize: 'var(--palette-light-font-size-small-value)'
                }}
              >
                {order.originalTotal}
              </Typography>
              <Typography 
                className="vendor-orders-table-text-primary" 
                component="div"
                sx={{ 
                  fontWeight: 600,
                  color: 'var(--palette-light-text-primary-value-hex)'
                }}
              >
                {order.total}
              </Typography>
            </Box>
          ) : (
            <Typography className="vendor-orders-table-text-primary" component="div">
              {order.total}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: 1,
      render: (order: Order) => {
        const statusConfig = getStatusConfig(order.status);
        return (
          <Box className="vendor-orders-table-cell-status">
            <Chip
              label={statusConfig.label}
              className="vendor-orders-table-status-chip"
              sx={{
                backgroundColor: statusConfig.backgroundColor,
                color: statusConfig.color,
              }}
            />
          </Box>
        );
      },
    },
      {
        key: 'actions',
        label: '',
        width: '56px',
        align: 'center',
        render: (order: Order) => (
          <Box className="vendor-orders-table-cell-actions">
            <IconButton 
              className="vendor-orders-table-action-button"
              onClick={(e) => {
                e.stopPropagation();
                onOrderClick?.(order);
              }}
            >
              <MoreHorizIcon className="vendor-orders-table-action-icon" />
            </IconButton>
          </Box>
        ),
      },
  ];

  return (
    <Box className="vendor-orders-table-wrapper">
      <Table
        columns={columns}
        data={orders}
        keyExtractor={(order) => order.id || order.orderNumber || `order-${orders.indexOf(order)}`}
        emptyMessage="No orders found"
        loading={loading}
      />
    </Box>
  );
};

export default VendorOrdersTable;

